<script setup>
import { ref, reactive, computed, onMounted, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { formatarDataBR } from '@/utils/useDataBR'
import { CNT_CMB_ST } from '@/constants/CNT_CMB_ST'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({})

const filtros = reactive({ busca: '', idInstituicaoFinanceira: '', lstStatusCambio: '' })

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('FECHAMENTOS_CAMBIO_COMPLETO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || authStore.pode('FECHAMENTOS_CAMBIO_VISUALIZACAO'))

const colunas = computed(() => {
  const cols = [
    { field: 'idContratoCambio', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'numeroContrato', label: 'Contrato', width: 150, sortable: true, class: 'fw-bold' },
    { field: 'lstStatusCambio', label: 'Status', width: 150, sortable: true, slotName: 'statusCambio' },
    { field: 'nomeInstituicaoFinanceira', label: 'Instituição Financeira', sortable: true },
    { field: 'lstTipoOperacaoCambio', label: 'Tipo Operação', align: 'center', sortable: true, slotName: 'tipoOperacao' },
    { field: 'dataFechamento', label: 'Fechamento', align: 'center', sortable: true, slotName: 'dataFechamento' },
    { field: 'dataVencimento', label: 'Vencimento', align: 'center', sortable: true, slotName: 'dataVencimento' },
    { field: 'valorMoedaEstrangeira', label: 'Valor Estrangeiro', align: 'right', sortable: true, slotName: 'valor' },
    { field: 'taxaCambialFechada', label: 'Taxa Fechada', align: 'right', sortable: true, slotName: 'taxa' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const obterClasseStatus = (status) => {
  if (!status) return 'badge bg-secondary'
  switch (status) {
    case CNT_CMB_ST.EmDigitacao:
      return 'badge bg-warning text-dark'
    case CNT_CMB_ST.Contratado:
      return 'badge bg-success'
    case CNT_CMB_ST.LiquidadoParcialmente:
      return 'badge bg-info text-dark'
    case CNT_CMB_ST.LiquidadoTotal:
      return 'badge bg-primary'
    case CNT_CMB_ST.Expirado:
      return 'badge bg-danger'
    case CNT_CMB_ST.Baixado:
      return 'badge bg-secondary'
    case CNT_CMB_ST.Cancelado:
      return 'badge bg-dark'
    default:
      return 'badge bg-secondary'
  }
}

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstTipoOperacaoCambio: (row) => getTextoLista(listas.LstTipoOperacaoCambio, row.lstTipoOperacaoCambio) || '-',
    lstStatusCambio: (row) => getTextoLista(listas.LstStatusCambio, row.lstStatusCambio) || '-'
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

const formatarMoeda = (valor, _moeda) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ContratoCambio'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

function getTextoLista (lista, id) {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id)
  return found ? found.textoLista : (id || '-')
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('termoBusca', filtros.busca)
    if (filtros.idInstituicaoFinanceira) params.append('idInstituicaoFinanceira', filtros.idInstituicaoFinanceira)
    if (filtros.lstStatusCambio) params.append('lstStatusCambio', filtros.lstStatusCambio)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/ContratosCambio?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.idInstituicaoFinanceira = ''; filtros.lstStatusCambio = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = (item) => { emit('editar-item', item) }

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idContratoCambio
  auditoriaSelecionada.nome = item.numeroContrato
  mostrarAuditoria.value = true
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Fechamentos de Câmbio"
    subtitulo="Gestão de contratos de câmbio pronto (Spot) e liquidação de faturas"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Nº Contrato ou Obs..." @keyup.enter="aplicarFiltros" />
        </div>
        <div class="col-md-3">
          <CampoBuscaLista
            id="filtroStatusCambio"
            label="Status"
            v-model="filtros.lstStatusCambio"
            prefixo="CNT_CMB_ST"
            contexto="Sistema"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Instituição Financeira</label>
          <select class="form-select" v-model="filtros.idInstituicaoFinanceira" @change="aplicarFiltros">
            <option value="">Todos os Bancos</option>
            <option v-for="b in listas.instituicoes" :key="b.idParceiro" :value="b.idParceiro">{{ b.nomeFantasia || b.razaoSocial }}</option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum fechamento de câmbio encontrado."
      row-key="idContratoCambio"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idContratoCambio }}</span>
      </template>
      <template #statusCambio="{ row }">
        <span :class="obterClasseStatus(row.lstStatusCambio)">{{ getTextoLista(listas.LstStatusCambio, row.lstStatusCambio) }}</span>
      </template>
      <template #tipoOperacao="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getTextoLista(listas.LstTipoOperacaoCambio, row.lstTipoOperacaoCambio) }}
        </span>
      </template>

      <template #dataFechamento="{ row }">
        {{ formatarDataBR(row.dataFechamento) }}
      </template>

      <template #dataVencimento="{ row }">
        {{ formatarDataBR(row.dataVencimento) }}
      </template>

      <template #valor="{ row }">
        {{ formatarMoeda(row.valorMoedaEstrangeira, row.lstMoedaEstrangeira) }}
      </template>

      <template #taxa="{ row }">
        <span class="fw-medium">{{ row.taxaCambialFechada?.toFixed(4) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ContratoCambio"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
